import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import Badge from '../components/ui/Badge';
import RecipientMultiSelect from '../components/RecipientMultiSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { useSettings } from '../store/SettingsContext';
import Icons from '../components/Icons';
import AddressService from '../services/AddressService';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import EmailService from '../services/EmailService';
import ExternalMessageService from '../services/ExternalMessageService';
import { buildResetToken } from '../services/PasswordService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { getStudentPortalLoginUrl } from '../utils/loginLinks';

const emptyContact = {
  type: 'guardian',
  role: 'Parent / Guardian',
  name: '',
  phone: '',
  email: '',
  notes: ''
};

const roleDefaults = {
  guardian: 'Parent / Guardian',
  social_worker: 'Social Worker',
  representative: 'Representative'
};

const DAY_LABELS = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun'
};

const normalizeProgrammeDay = (value) => {
  const key = String(value || '').slice(0, 3).toLowerCase();
  return DAY_LABELS[key] || value;
};

const parseProgrammeDays = (dayString) => {
  if (!dayString) return [];
  const parts = dayString
    .replace(/&/g, '/')
    .replace(/,/g, '/')
    .replace(/\s+/g, '')
    .split('/');
  return [...new Set(parts.map(normalizeProgrammeDay))].filter(Boolean);
};

export default function StudentFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { controller } = useStudentStore();
  const { user } = useUser();
  const { settings } = useSettings();
  const addressService = useMemo(() => new AddressService(), []);
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const staffOptions = useMemo(
    () =>
      staffList.map((worker) => ({
        value: worker.id,
        label: worker.name,
        meta: worker.role || worker.title || ''
      })),
    [staffList]
  );
  const staffRecipientOptions = useMemo(
    () =>
      staffList.map((worker) => ({
        id: worker.id,
        name: worker.name,
        meta: worker.role || worker.title || '',
        group: worker.team || worker.role || 'Staff'
      })),
    [staffList]
  );
  const initRef = useRef(false);
  const existingStudent = id ? controller.getStudent(id) : null;
  const isEdit = Boolean(existingStudent);
  const showOnlyProgrammes = location.hash === '#programmes';

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [status, setStatus] = useState('Active');
  const [school, setSchool] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [studentNotes, setStudentNotes] = useState('');
  const [addressLines, setAddressLines] = useState(['', '', '', '']);
  const [contacts, setContacts] = useState([emptyContact]);
  const [riskLevel, setRiskLevel] = useState('low');
  const [supportAreas, setSupportAreas] = useState([]);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [primaryWorkerId, setPrimaryWorkerId] = useState('');
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [programmeAssignments, setProgrammeAssignments] = useState([]);
  const [activityAssignments, setActivityAssignments] = useState([]);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [laFiles, setLaFiles] = useState([]);
  const [ucFiles, setUcFiles] = useState([]);
  const [medicalFiles, setMedicalFiles] = useState([]);
  const [policeFiles, setPoliceFiles] = useState([]);
  const [mashFiles, setMashFiles] = useState([]);

  useEffect(() => {
    initRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!existingStudent || initRef.current) return;
    initRef.current = true;
    if (existingStudent.firstName || existingStudent.lastName) {
      setFirstName(existingStudent.firstName || '');
      setMiddleName(existingStudent.middleName || '');
      setLastName(existingStudent.lastName || '');
    } else if (existingStudent.name) {
      const parts = existingStudent.name.split(' ').filter(Boolean);
      setFirstName(parts[0] || '');
      setLastName(parts.length > 1 ? parts[parts.length - 1] : '');
      setMiddleName(parts.length > 2 ? parts.slice(1, -1).join(' ') : '');
    }
    setDob(existingStudent.dob || '');
    setPronouns(existingStudent.pronouns || '');
    setStatus(existingStudent.status || 'Active');
    setSchool(existingStudent.school || '');
    setPhone(existingStudent.phone || '');
    setEmail(existingStudent.email || '');
    setStudentNotes(existingStudent.notesSummary || '');
    setSupportAreas(existingStudent.supportAreas || []);
    const nextAddress = [...(existingStudent.address || [])];
    while (nextAddress.length < 4) nextAddress.push('');
    setAddressLines(nextAddress.slice(0, 4));
    const supportWorkers = existingStudent.supportWorkers?.length
      ? existingStudent.supportWorkers
      : existingStudent.keyWorker
      ? [existingStudent.keyWorker]
      : [];
    const supportIds = supportWorkers.map((worker) => worker.id).filter(Boolean);
    setSelectedWorkerIds(supportIds);
    setPrimaryWorkerId(existingStudent.keyWorker?.id || supportIds[0] || '');
    const enrolments = (existingStudent.enrolments || []).map((enrol) =>
      enrol.toJSON ? enrol.toJSON() : enrol
    );
    const nextAssignments = enrolments.map((enrol) => {
      const programme =
        programmes.find((item) => item.name === enrol.programme) ||
        programmes.find((item) => item.id === enrol.programmeId);
      const programmeId = programme?.id || enrol.programmeId || `prog-${enrol.id || enrol.programme}`;
      const days =
        enrol.days && enrol.days.length
          ? enrol.days
          : programme?.days?.length
          ? programme.days
          : programme
          ? parseProgrammeDays(programme.day)
          : existingStudent.attendance?.scheduled || [];
      return createProgrammeRow({
        programmeId,
        programmeName: enrol.programme,
        days,
        startDate: enrol.startDate,
        endDate: enrol.endDate
      });
    });
    setProgrammeAssignments(nextAssignments.length ? nextAssignments : [createProgrammeRow()]);
    const existingActivities = (existingStudent.activities || []).map((activity) =>
      activity.toJSON ? activity.toJSON() : activity
    );
    const nextActivityAssignments = existingActivities.map((activity) => {
      const activityRecord =
        activities.find((item) => item.id === activity.activityId) ||
        activities.find((item) => item.name === activity.name);
      const activityId = activityRecord?.id || activity.activityId || '';
      return createActivityRow({
        activityId,
        activityName: activityRecord?.name || activity.name || '',
        date: activity.date || activityRecord?.date || '',
        status: activity.status || activityRecord?.status || 'scheduled'
      });
    });
    setActivityAssignments(nextActivityAssignments.length ? nextActivityAssignments : [createActivityRow()]);
    setContacts(
      existingStudent.contacts?.length
        ? existingStudent.contacts.map((contact) => ({
            ...contact,
            role: contact.role || roleDefaults[contact.type] || contact.role
          }))
        : [emptyContact]
    );
    setRiskLevel(existingStudent.riskLevel || 'low');
  }, [existingStudent, programmes, activities]);

  useEffect(() => {
    if (existingStudent) return;
    if (programmeAssignments.length === 0) {
      setProgrammeAssignments([createProgrammeRow()]);
    }
  }, [existingStudent, programmeAssignments.length]);

  useEffect(() => {
    if (existingStudent) return;
    if (activityAssignments.length === 0) {
      setActivityAssignments([createActivityRow()]);
    }
  }, [existingStudent, activityAssignments.length]);

  useEffect(() => {
    const query = addressQuery.trim();
    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressError('');
      return;
    }
    let active = true;
    const handle = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await addressService.search(query);
        if (active) {
          setAddressSuggestions(results);
          setAddressError('');
        }
      } catch (error) {
        if (active) {
          setAddressSuggestions([]);
          setAddressError('Unable to fetch address suggestions.');
        }
      } finally {
        if (active) setAddressLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [addressQuery, addressService]);

  if (id && !existingStudent) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Student not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/students')}>
          Back to students
        </Button>
      </Card>
    );
  }

  const updateContact = (index, field, value) => {
    setContacts((prev) =>
      prev.map((contact, idx) => {
        if (idx !== index) return contact;
        if (field === 'type') {
          return { ...contact, type: value, role: roleDefaults[value] || contact.role };
        }
        return { ...contact, [field]: value };
      })
    );
  };

  const fileListToArray = (files) => Array.from(files || []);

  const buildDocumentsPayload = (studentDocs = []) => {
    const base = studentDocs.map((doc) => (doc.toJSON ? doc.toJSON() : doc));
    const buildDocs = (files, tag) =>
      files.map((file) => ({
        id: `doc-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name,
        tags: [tag],
        visibility: ['Admin', 'Manager', 'Staff'],
        versions: [],
        metadata: {
          type: file.type || 'file',
          size: `${Math.round(file.size / 1024)} KB`
        }
      }));
    return [
      ...base,
      ...buildDocs(supportingFiles, 'Supporting'),
      ...buildDocs(laFiles, 'Local Authority'),
      ...buildDocs(ucFiles, 'UC'),
      ...buildDocs(medicalFiles, 'Medical'),
      ...buildDocs(policeFiles, 'Police'),
      ...buildDocs(mashFiles, 'MASH')
    ];
  };

  const buildCertificatesPayload = (studentCerts = []) => {
    const base = studentCerts.map((cert) => (cert.toJSON ? cert.toJSON() : cert));
    const today = new Date().toISOString().slice(0, 10);
    const next = certificateFiles.map((file) => ({
      id: `cert-${Math.random().toString(36).slice(2, 9)}`,
      name: file.name.replace(/\.[^/.]+$/, ''),
      issueDate: today,
      expiryDate: null,
      fileName: file.name
    }));
    return [...base, ...next];
  };

  const addContact = () => setContacts((prev) => [...prev, { ...emptyContact }]);
  const removeContact = (index) => setContacts((prev) => prev.filter((_, idx) => idx !== index));
  const updateAddressLine = (index, value) => {
    setAddressLines((prev) => {
      const next = [...prev];
      while (next.length < 4) next.push('');
      next[index] = value;
      return next;
    });
  };

  const applyAddressSuggestion = (suggestion) => {
    const next = [...(suggestion.lines || [])];
    while (next.length < 4) next.push('');
    setAddressLines(next.slice(0, 4));
    setAddressQuery(suggestion.label || '');
    setAddressSuggestions([]);
  };

  const supportAreaOptions = [
    'Mental Health',
    'Attendance',
    'Housing',
    'Family Support',
    'Safeguarding',
    'Substance Misuse',
    'Education',
    'Employment',
    'Financial Support',
    'Medical',
    'Legal',
    'Community Engagement'
  ];

  const toggleSupportArea = (area) => {
    setSupportAreas((prev) =>
      prev.includes(area) ? prev.filter((item) => item !== area) : [...prev, area]
    );
  };

  const dayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getProgrammeColorClass = (programmeId) => {
    const palette = ['sp-program--blue', 'sp-program--teal', 'sp-program--orange', 'sp-program--green', 'sp-program--rose'];
    const idx = programmes.findIndex((item) => item.id === programmeId);
    return palette[(idx >= 0 ? idx : 0) % palette.length];
  };

  const createProgrammeRow = (overrides = {}) => ({
    rowId: `prog-${Math.random().toString(36).slice(2, 9)}`,
    programmeId: overrides.programmeId || '',
    programmeName: overrides.programmeName || '',
    days: overrides.days || [],
    startDate: overrides.startDate || new Date().toISOString().slice(0, 10),
    endDate: overrides.endDate || null
  });

  const addProgrammeRow = () => {
    setProgrammeAssignments((prev) => [...prev, createProgrammeRow()]);
  };

  const removeProgrammeRow = (rowId) => {
    setProgrammeAssignments((prev) => prev.filter((assignment) => assignment.rowId !== rowId));
  };

  const updateProgrammeRow = (rowId, updates) => {
    setProgrammeAssignments((prev) =>
      prev.map((assignment) => (assignment.rowId === rowId ? { ...assignment, ...updates } : assignment))
    );
  };

  const handleProgrammeChange = (rowId, programmeId) => {
    if (!programmeId) {
      updateProgrammeRow(rowId, { programmeId: '', programmeName: '', days: [] });
      return;
    }
    const programme = programmes.find((item) => item.id === programmeId);
    if (!programme) return;
    updateProgrammeRow(rowId, {
      programmeId,
      programmeName: programme.name,
      days: programme.days?.length ? programme.days : parseProgrammeDays(programme.day)
    });
  };

  const toggleProgrammeDay = (rowId, day) => {
    const assignment = programmeAssignments.find((item) => item.rowId === rowId);
    if (!assignment) return;
    const nextDays = assignment.days.includes(day)
      ? assignment.days.filter((item) => item !== day)
      : [...assignment.days, day];
    updateProgrammeRow(rowId, { days: nextDays });
  };

  const createActivityRow = (overrides = {}) => ({
    rowId: `act-${Math.random().toString(36).slice(2, 9)}`,
    activityId: overrides.activityId || '',
    activityName: overrides.activityName || '',
    date: overrides.date || '',
    status: overrides.status || 'scheduled'
  });

  const addActivityRow = () => {
    setActivityAssignments((prev) => [...prev, createActivityRow()]);
  };

  const removeActivityRow = (rowId) => {
    setActivityAssignments((prev) => prev.filter((assignment) => assignment.rowId !== rowId));
  };

  const updateActivityRow = (rowId, updates) => {
    setActivityAssignments((prev) =>
      prev.map((assignment) => (assignment.rowId === rowId ? { ...assignment, ...updates } : assignment))
    );
  };

  const handleActivityChange = (rowId, activityId) => {
    if (!activityId) {
      updateActivityRow(rowId, { activityId: '', activityName: '', date: '', status: 'scheduled' });
      return;
    }
    const activity = activities.find((item) => item.id === activityId);
    if (!activity) return;
    updateActivityRow(rowId, {
      activityId,
      activityName: activity.name,
      date: activity.date || '',
      status: activity.status || 'scheduled'
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;

    const fullName = [firstName, middleName, lastName].filter((part) => part.trim()).join(' ');
    const payload = {
      name: fullName,
      firstName,
      middleName,
      lastName,
      dob,
      age: dob ? Math.max(0, new Date().getFullYear() - new Date(dob).getFullYear()) : null,
      pronouns,
      status,
      school,
      phone,
      email,
      notesSummary: studentNotes,
      supportAreas,
      riskLevel,
      address: addressLines.map((line) => line.trim()).filter(Boolean),
      contacts: contacts.filter((c) => c.name.trim())
    };
    const staffById = new Map(staffList.map((staff) => [staff.id, staff]));
    const selectedWorkers = selectedWorkerIds
      .map((workerId) => staffById.get(workerId))
      .filter(Boolean)
      .map((worker) => ({ id: worker.id, name: worker.name, role: worker.role }));
    const primaryWorker =
      staffById.get(primaryWorkerId) || (selectedWorkers.length ? staffById.get(selectedWorkers[0].id) : null);
    payload.supportWorkers = selectedWorkers;
    payload.keyWorker = primaryWorker
      ? {
          id: primaryWorker.id,
          name: primaryWorker.name,
          notes:
            existingStudent?.keyWorker?.id === primaryWorker.id
              ? existingStudent.keyWorker.notes || ''
              : '',
          goals:
            existingStudent?.keyWorker?.id === primaryWorker.id
              ? existingStudent.keyWorker.goals || []
              : []
        }
      : null;
    const existingAttendance =
      (existingStudent?.attendance?.toJSON && existingStudent.attendance.toJSON()) ||
      existingStudent?.attendance ||
      {};
    const programmeDays = programmeAssignments
      .filter((assignment) => assignment.programmeId)
      .flatMap((assignment) => assignment.days || []);
    const uniqueDays = [...new Set(programmeDays)];
    payload.attendance = {
      rate: existingAttendance.rate || 100,
      trend: existingAttendance.trend || 'stable',
      scheduled: uniqueDays.length ? uniqueDays : existingAttendance.scheduled || [],
      last8Weeks: existingAttendance.last8Weeks || [],
      absenceReasons: existingAttendance.absenceReasons || [],
      logs: existingAttendance.logs || []
    };
    const existingEnrolments = (existingStudent?.enrolments || []).map((enrol) =>
      enrol.toJSON ? enrol.toJSON() : enrol
    );
    const enrolmentMap = new Map(existingEnrolments.map((enrol) => [enrol.programme, enrol]));
    payload.enrolments = programmeAssignments
      .filter((assignment) => assignment.programmeId)
      .map((assignment) => {
        const existing = enrolmentMap.get(assignment.programmeName);
        return (
          existing || {
            id: `enrol-${Math.random().toString(36).slice(2, 9)}`,
            programme: assignment.programmeName,
            startDate: assignment.startDate || new Date().toISOString().slice(0, 10),
            endDate: assignment.endDate || null,
            days: assignment.days || []
          }
        );
      });
    const existingActivities = (existingStudent?.activities || []).map((activity) =>
      activity.toJSON ? activity.toJSON() : activity
    );
    const activityMap = new Map(
      existingActivities.map((activity) => [activity.activityId || activity.name, activity])
    );
    payload.activities = activityAssignments
      .filter((assignment) => assignment.activityId)
      .map((assignment) => {
        const existing =
          activityMap.get(assignment.activityId) || activityMap.get(assignment.activityName);
        const activity = activities.find((item) => item.id === assignment.activityId);
        return (
          existing || {
            id: `act-enr-${Math.random().toString(36).slice(2, 9)}`,
            activityId: assignment.activityId,
            name: assignment.activityName,
            date: assignment.date || activity?.date || '',
            time: activity?.time || '',
            location: activity?.location || '',
            status: assignment.status || activity?.status || 'scheduled'
          }
        );
      });
    payload.documents = buildDocumentsPayload(existingStudent?.documents || []);
    payload.certificates = buildCertificatesPayload(existingStudent?.certificates || []);

    let student;
    if (isEdit) {
      student = controller.updateStudent(id, payload, user);
    } else {
      student = controller.createStudent(
        {
          ...payload,
          vulnerabilities: [],
          safeguardingEvents: [],
          medicalEvents: [],
          policeEvents: [],
          notes: [],
          statusHistory: [{ status, date: new Date().toISOString().slice(0, 10), by: user.name }]
        },
        user
      );
    }

    if (!isEdit && student) {
      const siteName = settings.siteName || 'SafeguardPro';
      const studentPortalLoginUrl = getStudentPortalLoginUrl();
      let resetLink = '';
      let resetExpiresAt = '';

      if (student.email) {
        const { token, tokenHash, expiresAt } = await buildResetToken();
        controller.updateStudent(
          student.id,
          {
            portalResetTokenHash: tokenHash,
            portalResetExpiresAt: expiresAt
          },
          user
        );
        resetLink =
          typeof window !== 'undefined'
            ? `${window.location.origin}/student-portal/reset?token=${token}`
            : `/student-portal/reset?token=${token}`;
        resetExpiresAt = expiresAt;

        emailService.sendTemplate({
          templateKey: 'student.portal_reset',
          to: student.email,
          data: {
            student_name: student.name,
            action_url: resetLink,
            login_url: studentPortalLoginUrl,
            expires_at: formatDateTime(expiresAt),
            site_name: siteName
          },
          entity: { type: 'student', id: student.id }
        });
      }

      externalMessageService.sendFromStaff({
        subject: 'Student portal access ready',
        body: [
          `Hi ${student.name},`,
          `Your ${siteName} student portal access is ready.`,
          `Student portal login: ${studentPortalLoginUrl}`,
          resetLink
            ? `Set your password: ${resetLink}${resetExpiresAt ? ` (expires ${formatDateTime(resetExpiresAt)})` : ''}`
            : 'Contact your support worker if you need a password reset link.'
        ].join('\n\n'),
        fromUser: user || { id: 'system', name: siteName },
        recipients: [{ id: student.id, name: student.name, type: 'student' }],
        category: 'onboarding',
        references: [
          {
            type: 'student',
            id: student.id,
            label: student.name,
            route: `/students/${student.id}/dashboard`
          }
        ]
      });
    }

    if (showOnlyProgrammes) {
      navigate(`/students/${student.id}/profile`);
      return;
    }
    navigate(`/students/${student.id}/dashboard`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>
            {showOnlyProgrammes
              ? 'Edit Programmes & Activities'
              : isEdit
              ? 'Edit Student Profile'
              : 'Enrol Student'}
          </h2>
          <p className="text-sm sp-muted">
            {showOnlyProgrammes
              ? 'Update programme and activity assignments.'
              : isEdit
              ? 'Update existing student information'
              : 'Create a full profile for safeguarding'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => (isEdit ? navigate(`/students/${id}/profile`) : navigate('/students'))}
          >
            {showOnlyProgrammes ? 'Back to profile' : 'Cancel'}
          </Button>
          <Button type="submit">{showOnlyProgrammes ? 'Save Schedule' : 'Save Student'}</Button>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Basic Info</h3>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="sp-label">First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="First name"
              required
            />
          </div>
          <div>
            <label className="sp-label">Middle name</label>
            <input
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="Middle name"
            />
          </div>
          <div>
            <label className="sp-label">Surname</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="Surname"
              required
            />
          </div>
          <div>
            <label className="sp-label">Date of birth</label>
            <DateSelect value={dob} onChange={setDob} className="mt-2 w-full" />
          </div>
          <div>
            <label className="sp-label">Pronouns</label>
            <input
              value={pronouns}
              onChange={(e) => setPronouns(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="e.g. she/her"
            />
          </div>
          <div>
            <label className="sp-label">School</label>
            <input
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="School name"
            />
          </div>
          <div>
            <label className="sp-label">Telephone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="Phone number"
              type="tel"
              autoComplete="tel"
            />
          </div>
          <div>
            <label className="sp-label">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="sp-input mt-2 w-full"
              placeholder="Email address"
              type="email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="sp-label">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="sp-select mt-2 w-full"
            >
              <option>Active</option>
              <option>Inactive</option>
              <option>Archived</option>
              <option>Under Review</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Risk level</label>
            <select
              value={riskLevel}
              onChange={(e) => setRiskLevel(e.target.value)}
              className="sp-select mt-2 w-full"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="sp-label">Notes</label>
            <textarea
              value={studentNotes}
              onChange={(e) => setStudentNotes(e.target.value)}
              className="sp-textarea mt-2 w-full"
              rows={3}
              placeholder="Key notes for the student profile"
            />
          </div>
          <div className="md:col-span-2">
            <label className="sp-label">Address (multiline)</label>
            <div className="mt-2">
              <div className="sp-search">
                <Icons.Search />
                <input
                  value={addressQuery}
                  onChange={(e) => setAddressQuery(e.target.value)}
                  placeholder="Start typing to search addresses"
                  aria-label="Search address"
                  autoComplete="street-address"
                />
                {addressLoading && <span className="text-xs sp-muted">Searching…</span>}
              </div>
              {addressError && <div className="text-xs sp-muted mt-2">{addressError}</div>}
              {addressSuggestions.length > 0 && (
                <div className="sp-panel sp-panel--muted mt-2">
                  {addressSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      className="sp-suggestion"
                      onClick={() => applyAddressSuggestion(suggestion)}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-2 grid gap-2">
              <input
                value={addressLines[0] ?? ''}
                onChange={(e) => updateAddressLine(0, e.target.value)}
                className="sp-input w-full"
                placeholder="Address line 1"
                autoComplete="address-line1"
              />
              <input
                value={addressLines[1] ?? ''}
                onChange={(e) => updateAddressLine(1, e.target.value)}
                className="sp-input w-full"
                placeholder="Address line 2"
                autoComplete="address-line2"
              />
              <input
                value={addressLines[2] ?? ''}
                onChange={(e) => updateAddressLine(2, e.target.value)}
                className="sp-input w-full"
                placeholder="City / Town"
                autoComplete="address-level2"
              />
              <input
                value={addressLines[3] ?? ''}
                onChange={(e) => updateAddressLine(3, e.target.value)}
                className="sp-input w-full"
                placeholder="Postcode"
                autoComplete="postal-code"
              />
            </div>
          </div>
        </div>
      </Card>

      {!showOnlyProgrammes && (
      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Areas of Support</h3>
        <p className="text-sm sp-muted mt-1">Select the focus areas for this student.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {supportAreaOptions.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() => toggleSupportArea(area)}
              className={`sp-badge ${supportAreas.includes(area) ? 'sp-badge--active' : ''}`}
            >
              {area}
            </button>
          ))}
          {supportAreas.length === 0 && (
            <Badge variant="info" className="sp-badge--subtle">No areas selected</Badge>
          )}
        </div>
      </Card>
      )}

      {!showOnlyProgrammes && (
      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Contact Details</h3>
        <p className="text-sm sp-muted mt-1">Add parents, guardians, social workers, or representatives.</p>
        <div className="space-y-4 mt-4">
          {contacts.map((contact, index) => (
            <div key={index} className="sp-panel p-4">
              <div className="flex items-center justify-between mb-3">
                <Badge variant="info" className="sp-badge--subtle">Contact {index + 1}</Badge>
                {contacts.length > 1 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeContact(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sp-label">Type</label>
                  <select
                    value={contact.type}
                    onChange={(e) => updateContact(index, 'type', e.target.value)}
                    className="sp-select mt-2 w-full"
                  >
                    <option value="guardian">Parent / Guardian</option>
                    <option value="social_worker">Social Worker</option>
                    <option value="representative">Representative</option>
                  </select>
                </div>
                <div>
                  <label className="sp-label">Role</label>
                  <input
                    value={contact.role}
                    onChange={(e) => updateContact(index, 'role', e.target.value)}
                    className="sp-input mt-2 w-full"
                  />
                </div>
                <div>
                  <label className="sp-label">Name</label>
                  <input
                    value={contact.name}
                    onChange={(e) => updateContact(index, 'name', e.target.value)}
                    className="sp-input mt-2 w-full"
                  />
                </div>
                <div>
                  <label className="sp-label">Phone</label>
                  <input
                    value={contact.phone}
                    onChange={(e) => updateContact(index, 'phone', e.target.value)}
                    className="sp-input mt-2 w-full"
                  />
                </div>
                <div>
                  <label className="sp-label">Email</label>
                  <input
                    value={contact.email}
                    onChange={(e) => updateContact(index, 'email', e.target.value)}
                    className="sp-input mt-2 w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">Notes</label>
                  <textarea
                    value={contact.notes}
                    onChange={(e) => updateContact(index, 'notes', e.target.value)}
                    className="sp-textarea mt-2 w-full"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button type="button" variant="secondary" className="mt-4" onClick={addContact}>
          Add Contact
        </Button>
      </Card>
      )}

      {!showOnlyProgrammes && (
      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assigned Support Workers</h3>
        <p className="text-sm sp-muted mt-1">Select one primary worker and any additional workers.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="sp-label">Primary worker</label>
            <SearchableSelect
              value={primaryWorkerId}
              options={[{ value: '', label: 'Unassigned' }, ...staffOptions]}
              placeholder="Search staff"
              emptyLabel="No staff found."
              ariaLabel="Search primary worker"
              className="mt-2"
              onChange={(value) => {
                const nextId = value;
                setPrimaryWorkerId(nextId);
                if (nextId && !selectedWorkerIds.includes(nextId) && nextId) {
                  setSelectedWorkerIds((prev) => [...prev, nextId]);
                }
                if (!nextId) {
                  setSelectedWorkerIds((prev) => prev.filter((id) => id !== primaryWorkerId));
                }
              }}
            />
            {primaryWorkerId && (
              <div className="text-xs sp-muted mt-2">Primary worker is also included in additional workers.</div>
            )}
          </div>
          <div>
            <RecipientMultiSelect
              label="Additional workers"
              placeholder="Search by name or role"
              options={staffRecipientOptions}
              selectedIds={selectedWorkerIds}
              allowNone
              helperText="Use quick search to add multiple staff. Selected workers appear as chips."
              onChange={(nextIds) => {
                const ensurePrimary = primaryWorkerId && !nextIds.includes(primaryWorkerId)
                  ? [...nextIds, primaryWorkerId]
                  : nextIds;
                setSelectedWorkerIds(ensurePrimary);
                if (primaryWorkerId && !ensurePrimary.includes(primaryWorkerId)) {
                  setPrimaryWorkerId('');
                }
              }}
            />
          </div>
        </div>
      </Card>
      )}

      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Programmes & Activities</h3>
        <p className="text-sm sp-muted mt-1">Add each programme separately with its own schedule.</p>
        <div className="space-y-4 mt-4">
          {programmeAssignments.map((assignment, index) => {
            const selectedIds = programmeAssignments
              .filter((row) => row.rowId !== assignment.rowId)
              .map((row) => row.programmeId)
              .filter(Boolean);
            return (
              <div key={assignment.rowId} className="sp-panel sp-panel--muted p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="info" className="sp-badge--subtle">Programme {index + 1}</Badge>
                    {assignment.programmeId && (
                      <span className={`sp-program ${getProgrammeColorClass(assignment.programmeId)}`}>
                        {assignment.programmeName}
                      </span>
                    )}
                  </div>
                  {programmeAssignments.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeProgrammeRow(assignment.rowId)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="sp-label">Programme</label>
                    <select
                      value={assignment.programmeId}
                      onChange={(e) => handleProgrammeChange(assignment.rowId, e.target.value)}
                      className="sp-select mt-2 w-full"
                    >
                      <option value="">Select programme</option>
                      {programmes.map((programme) => (
                        <option
                          key={programme.id}
                          value={programme.id}
                          disabled={selectedIds.includes(programme.id)}
                        >
                          {programme.name} — {programme.day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="sp-label">Programme days</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {dayOptions.map((day) => (
                        <label key={day} className="flex items-center gap-2 text-sm sp-muted">
                          <input
                            type="checkbox"
                            checked={assignment.days.includes(day)}
                            onChange={() => toggleProgrammeDay(assignment.rowId, day)}
                            disabled={!assignment.programmeId}
                          />
                          <span>{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Button type="button" variant="secondary" className="mt-4" onClick={addProgrammeRow}>
          Add Programme
        </Button>
      </Card>

      <Card className="p-6">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>One‑off Activities</h3>
        <p className="text-sm sp-muted mt-1">Short events and one‑off activities for this student.</p>
        <div className="space-y-4 mt-4">
          {activityAssignments.map((assignment, index) => {
            const selectedIds = activityAssignments
              .filter((row) => row.rowId !== assignment.rowId)
              .map((row) => row.activityId)
              .filter(Boolean);
            const activity = activities.find((item) => item.id === assignment.activityId);
            return (
              <div key={assignment.rowId} className="sp-panel sp-panel--muted p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="info" className="sp-badge--subtle">Activity {index + 1}</Badge>
                    {assignment.activityName && (
                      <span className="text-xs sp-muted">{assignment.activityName}</span>
                    )}
                  </div>
                  {activityAssignments.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeActivityRow(assignment.rowId)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="sp-label">Activity</label>
                    <select
                      value={assignment.activityId}
                      onChange={(e) => handleActivityChange(assignment.rowId, e.target.value)}
                      className="sp-select mt-2 w-full"
                    >
                      <option value="">Select activity</option>
                      {activities.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={selectedIds.includes(item.id)}
                        >
                          {item.name} — {item.date ? formatDate(item.date) : 'TBD'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="sp-label">Status</label>
                    <select
                      value={assignment.status}
                      onChange={(e) => updateActivityRow(assignment.rowId, { status: e.target.value })}
                      className="sp-select mt-2 w-full"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 text-xs sp-muted">
                  {activity
                    ? `Date: ${activity.date ? formatDate(activity.date) : 'TBD'} · Time: ${activity.time || 'TBD'} · Location: ${activity.location || 'TBD'}`
                    : 'Pick an activity to see details.'}
                </div>
              </div>
            );
          })}
        </div>
        <Button type="button" variant="secondary" className="mt-4" onClick={addActivityRow}>
          Add Activity
        </Button>
      </Card>

      {!showOnlyProgrammes && (
        <>
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Photo Upload</h3>
            <input type="file" className="sp-input mt-3 w-full" />
            <p className="text-xs sp-muted mt-2">Uploads are stored locally for now.</p>
          </Card>

          <Card className="p-6">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Document Uploads</h3>
            <p className="text-sm sp-muted mt-1">
              Upload certificates and supporting evidence. Files are stored locally for now.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="sp-label">Certificates</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setCertificateFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div>
                <label className="sp-label">Supporting Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setSupportingFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div>
                <label className="sp-label">Local Authority Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setLaFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div>
                <label className="sp-label">UC Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setUcFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div>
                <label className="sp-label">Medical Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setMedicalFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div>
                <label className="sp-label">Police Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setPoliceFiles(fileListToArray(e.target.files))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="sp-label">MASH Documents</label>
                <input
                  type="file"
                  multiple
                  className="sp-input mt-2 w-full"
                  onChange={(e) => setMashFiles(fileListToArray(e.target.files))}
                />
              </div>
            </div>
          </Card>
        </>
      )}
    </form>
  );
}
